import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle,
  Code,
  Copy,
  MessageSquare,
  Play,
  Send,
  Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { FpMessage, FpMessagePart } from "../../../../../schemas/codegen";
import { Message } from "./Message";
import { useMessageContent } from "./useMessageContent";
import { useToolCallExpansion } from "./useToolCallExpansion";

// Type guards for message parts
const isTextPart = (
  part: FpMessagePart,
): part is Extract<FpMessagePart, { type: "text" }> => part.type === "text";

const isToolCallPart = (
  part: FpMessagePart,
): part is Extract<FpMessagePart, { type: "tool-call" }> =>
  part.type === "tool-call";

interface ChatInterfaceProps {
  messages: FpMessage[];
  connectionStatus: string;
  onSendMessage: (message: string) => void;
}

export function ChatInterface({
  messages,
  connectionStatus,
  onSendMessage,
}: ChatInterfaceProps) {
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Custom hooks
  const { getCombinedMessageContent } = useMessageContent();
  const { expandedToolCalls, toggleToolCallExpansion } = useToolCallExpansion();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  });

  const handleSendMessage = () => {
    if (inputValue.trim()) {
      onSendMessage(inputValue.trim());
      setInputValue("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1">
      {/* Main Chat Interface */}
      <div className="lg:col-span-2 flex flex-col">
        <Card className="flex-1 flex flex-col">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              MCP Debug Chat
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge
                variant={
                  connectionStatus === "connected" ? "default" : "secondary"
                }
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                {connectionStatus === "connected"
                  ? "Connected"
                  : "Disconnected"}
              </Badge>
              <Link to="/projects">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Projects
                </Button>
              </Link>
            </div>
          </CardHeader>

          <CardContent className="flex-1 flex flex-col">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto space-y-2 mb-4 p-2">
              {messages.map((message) => {
                const combinedContent = getCombinedMessageContent(message);
                return (
                  <Message
                    key={message.id}
                    message={message}
                    combinedContent={combinedContent}
                    expandedToolCalls={expandedToolCalls}
                    onToggleToolCallExpansion={toggleToolCallExpansion}
                  />
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="flex gap-2">
              <Input
                placeholder="Debug your MCP server..."
                className="flex-1"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <Button
                onClick={handleSendMessage}
                disabled={
                  !inputValue.trim() || connectionStatus !== "connected"
                }
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Debug Panel */}
      <div className="space-y-4">
        {/* Active Session Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Debug Session</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Session ID</span>
              <span className="font-mono">dbg-a1b2c3</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Messages</span>
              <span>{messages.length}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Tool Calls</span>
              <span>
                {messages.reduce(
                  (count, msg) =>
                    count + (msg.parts?.filter(isToolCallPart).length || 0),
                  0,
                )}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Text Parts</span>
              <span>
                {messages.reduce(
                  (count, msg) =>
                    count + (msg.parts?.filter(isTextPart).length || 0),
                  0,
                )}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Context Size</span>
              <span>2.1KB</span>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
            >
              <Play className="w-4 h-4 mr-2" />
              Test Connection
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
            >
              <Code className="w-4 h-4 mr-2" />
              List Tools
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
            >
              <Zap className="w-4 h-4 mr-2" />
              List Resources
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
            >
              <Copy className="w-4 h-4 mr-2" />
              Export Session
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
